import { Input, Typography } from '@smartgift/frontend-toolkit';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTACT_LIMIT_NUMBER, DEFAULT_CONTACT_LIMIT_NUMBER } from '@hero/constants/merchant';
import { OnboardingCard, ShareAnywhere } from '@hero/molecules';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useGetRecognitionRecipientList } from '@hero/services/order-service';
import { useNewRecognitionStore, useOnboardingStore } from '@hero/store/hooks';

import * as S from './style';
import { WithoutContactRecipientVerification } from './WithoutContactRecipientVerification';

function SendWithoutContactCard() {
  const { t } = useTranslation();
  const { recognition: onboardingRecognition } = useOnboardingStore();

  const {
    code,
    settings: { recipientVerification },
    contacts,
    emails,
    updateRecipientVerification,
    updateContacts
  } = useNewRecognitionStore();

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: code
    },
    {
      enabled: !!code
    }
  );
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const [isOnboardingCardShown, setIsOnboardingCardShown] = useState(true);

  const [recipientQuantityRef, setRecipientQuantityRef] = useState();

  const contactNumberLimit =
    merchantData?.configs[CONTACT_LIMIT_NUMBER] ?? DEFAULT_CONTACT_LIMIT_NUMBER;

  const totalRecipients = useMemo(() => {
    if (code) {
      return recognitionRecipients?.totalDocs;
    }

    return emails.length;
  }, [recognitionRecipients, emails]);

  const maxContactHasError = useMemo(() => {
    return contacts?.maxCount >= contactNumberLimit;
  }, [merchantData, recipientVerification, totalRecipients, contacts?.maxCount]);

  return (
    <S.SendWithoutContactCardWrapper>
      <S.QuantityContainer>
        <OnboardingCard
          styles={{
            wrapper: {
              right: '8vw',
              top: '7vh'
            }
          }}
          cardOffset={[12, 10]}
          position="right-start"
          isInfoActive={onboardingRecognition?.infoCardsActive}
          featureType="recognition"
          isCardShown={isOnboardingCardShown}
          setIsCardShown={setIsOnboardingCardShown}
          title={t('enterRecipientQuantity')}
          description={t('enterRecipientQuantityDesc')}
        />
        <S.RecipientQuantityInputArea>
          <Typography style={{ marginBottom: 16 }} color="fg" variant="body1Bold">
            {t('enterTotalRecipients')}:
          </Typography>

          <Input
            ref={setRecipientQuantityRef}
            variant="outlined"
            error={maxContactHasError}
            value={contacts?.maxCount || ''}
            size="lg"
            name="recipientQuantity"
            label={`${t('recipientQuantity')}*`}
            min={1}
            maxLength={5}
            max={100000}
            onChange={event => {
              updateContacts({ maxCount: Number(event.target.value), type: 'number', ids: [] });
            }}
            style={{ width: 372 }}
          />

          {maxContactHasError && (
            <S.MaxContactError>
              {t(
                contacts?.maxCount >= contactNumberLimit
                  ? 'recipientLimitTitle'
                  : 'recipientEmailLimitTitle'
              )}
            </S.MaxContactError>
          )}
        </S.RecipientQuantityInputArea>
        <WithoutContactRecipientVerification
          recipientVerification={recipientVerification}
          updateRecipientVerification={updateRecipientVerification}
          recognitionCode={code}
          recipientQuantityRef={recipientQuantityRef}
        />
      </S.QuantityContainer>
      <ShareAnywhere />
    </S.SendWithoutContactCardWrapper>
  );
}

export { SendWithoutContactCard };
